// Tratamento das páginas do Scanner PDF (0.49), feito no próprio aparelho, sem servidor:
// endireitar a folha pelos 4 cantos (perspectiva), girar e os filtros Documento (tira sombra e clareia o fundo),
// Cinza e Preto e branco. Funciona igual no Android, no iPhone e no navegador.
import { decode, encode } from 'jpeg-js';

export type Img = { w: number; h: number; d: Uint8Array };   // RGBA
export type Ponto = { x: number; y: number };                 // de 0 a 1 (fração da largura e da altura)
export type Cantos = [Ponto, Ponto, Ponto, Ponto];           // sup. esq., sup. dir., inf. dir., inf. esq.
export type Filtro = 'original' | 'documento' | 'cinza' | 'pb';
export const FILTROS: Filtro[] = ['documento', 'original', 'cinza', 'pb'];
export const CANTOS_INTEIROS: Cantos = [{ x: 0, y: 0 }, { x: 1, y: 0 }, { x: 1, y: 1 }, { x: 0, y: 1 }];

export function decodificar(bytes: Uint8Array): Img {
  const r = decode(bytes, { useTArray: true, formatAsRGBA: true, maxMemoryUsageInMB: 512 });
  return { w: r.width, h: r.height, d: r.data as Uint8Array };
}

export function codificar(img: Img, qualidade = 82): Uint8Array {
  // o codificador usa Buffer.from no fim; no celular não existe Buffer, então damos um provisório só durante a chamada
  const g = globalThis as any;
  const tinha = 'Buffer' in g;
  if (!tinha) g.Buffer = { from: (a: ArrayLike<number>) => Uint8Array.from(a) };
  try {
    const r = encode({ width: img.w, height: img.h, data: img.d }, qualidade);
    return r.data instanceof Uint8Array ? r.data : Uint8Array.from(r.data as ArrayLike<number>);
  } finally { if (!tinha) delete g.Buffer; }
}

/** Os cantos cobrem a imagem inteira (nada a endireitar)? */
export const cantosInteiros = (c?: Cantos | null) => !c || c.every((p, i) => Math.abs(p.x - CANTOS_INTEIROS[i].x) < 0.004 && Math.abs(p.y - CANTOS_INTEIROS[i].y) < 0.004);

/** Endireita a folha: recorta pelos 4 cantos e corrige a perspectiva (foto tirada de lado). */
export function endireitar(src: Img, c: Cantos, maxLado = 2000): Img {
  const P = c.map(p => ({ x: p.x * (src.w - 1), y: p.y * (src.h - 1) }));
  const dist = (a: Ponto, b: Ponto) => Math.hypot(a.x - b.x, a.y - b.y);
  let W = Math.max(dist(P[0], P[1]), dist(P[3], P[2]));
  let H = Math.max(dist(P[0], P[3]), dist(P[1], P[2]));
  const k = Math.min(1, maxLado / Math.max(W, H));
  W = Math.max(8, Math.round(W * k)); H = Math.max(8, Math.round(H * k));
  // quadrado unitário -> quadrilátero (Heckbert)
  const [p0, p1, p2, p3] = P;
  const dx1 = p1.x - p2.x, dx2 = p3.x - p2.x, dx3 = p0.x - p1.x + p2.x - p3.x;
  const dy1 = p1.y - p2.y, dy2 = p3.y - p2.y, dy3 = p0.y - p1.y + p2.y - p3.y;
  let a: number, b: number, cc: number, d: number, e: number, f: number, g: number, h: number;
  const den = dx1 * dy2 - dx2 * dy1;
  if ((Math.abs(dx3) < 1e-6 && Math.abs(dy3) < 1e-6) || Math.abs(den) < 1e-9) {
    g = 0; h = 0; a = p1.x - p0.x; b = p3.x - p0.x; cc = p0.x; d = p1.y - p0.y; e = p3.y - p0.y; f = p0.y;
  } else {
    g = (dx3 * dy2 - dx2 * dy3) / den; h = (dx1 * dy3 - dx3 * dy1) / den;
    a = p1.x - p0.x + g * p1.x; b = p3.x - p0.x + h * p3.x; cc = p0.x;
    d = p1.y - p0.y + g * p1.y; e = p3.y - p0.y + h * p3.y; f = p0.y;
  }
  const out = new Uint8Array(W * H * 4);
  const sw = src.w, sh = src.h, sd = src.d;
  for (let y = 0; y < H; y++) {
    const v = y / (H - 1);
    for (let x = 0; x < W; x++) {
      const u = x / (W - 1);
      const z = g * u + h * v + 1;
      let X = (a * u + b * v + cc) / z, Y = (d * u + e * v + f) / z;
      if (X < 0) X = 0; else if (X > sw - 1) X = sw - 1;
      if (Y < 0) Y = 0; else if (Y > sh - 1) Y = sh - 1;
      const x0 = X | 0, y0 = Y | 0, x1 = x0 < sw - 1 ? x0 + 1 : x0, y1 = y0 < sh - 1 ? y0 + 1 : y0;
      const fx = X - x0, fy = Y - y0;
      const i00 = (y0 * sw + x0) * 4, i10 = (y0 * sw + x1) * 4, i01 = (y1 * sw + x0) * 4, i11 = (y1 * sw + x1) * 4;
      const o = (y * W + x) * 4;
      for (let ch = 0; ch < 3; ch++) {
        const top = sd[i00 + ch] + (sd[i10 + ch] - sd[i00 + ch]) * fx;
        const bot = sd[i01 + ch] + (sd[i11 + ch] - sd[i01 + ch]) * fx;
        out[o + ch] = top + (bot - top) * fy;
      }
      out[o + 3] = 255;
    }
  }
  return { w: W, h: H, d: out };
}

/** Gira 90, 180 ou 270 graus no sentido do relógio. */
export function girar(src: Img, graus: number): Img {
  const q = (((Math.round(graus / 90) % 4) + 4) % 4);
  if (!q) return src;
  const { w, h, d } = src;
  const W = q % 2 ? h : w, H = q % 2 ? w : h;
  const out = new Uint8Array(W * H * 4);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    let nx: number, ny: number;
    if (q === 1) { nx = h - 1 - y; ny = x; } else if (q === 2) { nx = w - 1 - x; ny = h - 1 - y; } else { nx = y; ny = w - 1 - x; }
    const i = (y * w + x) * 4, o = (ny * W + nx) * 4;
    out[o] = d[i]; out[o + 1] = d[i + 1]; out[o + 2] = d[i + 2]; out[o + 3] = 255;
  }
  return { w: W, h: H, d: out };
}

/** Mede o "papel" (o fundo claro) em blocos, para tirar sombras e deixar o fundo branco por igual. */
function fundo(src: Img): (x: number, y: number) => number {
  const { w, h, d } = src;
  const B = Math.max(12, Math.round(Math.min(w, h) / 28));
  const gw = Math.ceil(w / B), gh = Math.ceil(h / B);
  let grade = new Float32Array(gw * gh);
  for (let by = 0; by < gh; by++) for (let bx = 0; bx < gw; bx++) {
    // segundo maior brilho do bloco (amostrado), para um ponto de luz não enganar
    let m1 = 0, m2 = 0;
    for (let y = by * B; y < Math.min(h, (by + 1) * B); y += 2) for (let x = bx * B; x < Math.min(w, (bx + 1) * B); x += 2) {
      const i = (y * w + x) * 4;
      const l = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
      if (l > m1) { m2 = m1; m1 = l; } else if (l > m2) m2 = l;
    }
    grade[by * gw + bx] = Math.max(m2, 1);
  }
  // blocos cheios de texto ou de foto ficam escuros: usa o vizinho mais claro e depois suaviza
  for (let passe = 0; passe < 2; passe++) {
    const n = new Float32Array(gw * gh);
    for (let y = 0; y < gh; y++) for (let x = 0; x < gw; x++) {
      let m = 0, s = 0, k = 0;
      for (let j = -1; j <= 1; j++) for (let i = -1; i <= 1; i++) {
        const xx = x + i, yy = y + j;
        if (xx < 0 || yy < 0 || xx >= gw || yy >= gh) continue;
        const v = grade[yy * gw + xx]; if (v > m) m = v; s += v; k++;
      }
      n[y * gw + x] = passe === 0 ? Math.max(grade[y * gw + x], m * 0.9) : s / k;
    }
    grade = n;
  }
  return (x: number, y: number) => {
    const gx = Math.min(gw - 1, Math.max(0, x / B - 0.5)), gy = Math.min(gh - 1, Math.max(0, y / B - 0.5));
    const x0 = gx | 0, y0 = gy | 0, x1 = Math.min(gw - 1, x0 + 1), y1 = Math.min(gh - 1, y0 + 1);
    const fx = gx - x0, fy = gy - y0;
    const t = grade[y0 * gw + x0] + (grade[y0 * gw + x1] - grade[y0 * gw + x0]) * fx;
    const b = grade[y1 * gw + x0] + (grade[y1 * gw + x1] - grade[y1 * gw + x0]) * fx;
    return t + (b - t) * fy;
  };
}

export function filtrar(src: Img, filtro: Filtro): Img {
  if (filtro === 'original') return src;
  const { w, h, d } = src;
  const bg = fundo(src);
  const out = new Uint8Array(w * h * 4);
  // fator de cada pixel: o papel (um pouco abaixo do ponto mais claro do bloco) vira branco
  const fator = new Float32Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) fator[y * w + x] = 255 / (bg(x, y) * 0.93);
  // cor do papel (amarelado, azulado pela luz): mede nos pontos claros para deixar o fundo branco de verdade
  let sr = 0, sg = 0, sb = 0, n = 0;
  for (let p = 0; p < w * h; p += 7) {
    const i = p * 4, k = fator[p];
    const l = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) * k;
    if (l > 235 && l < 300) { sr += d[i] * k; sg += d[i + 1] * k; sb += d[i + 2] * k; n++; }
  }
  const ganho = n > 50 ? [sr, sg, sb].map(s => { const m = (sr + sg + sb) / 3; return Math.max(0.8, Math.min(1.25, m / s)); }) : [1, 1, 1];
  for (let p = 0; p < w * h; p++) {
    const i = p * 4, k = fator[p];
    if (filtro === 'documento') {
      for (let ch = 0; ch < 3; ch++) {
        let v = d[i + ch] * k * ganho[ch];   // fundo vira branco, sombra some
        v = (v - 30) * (255 / 225);          // texto mais escuro
        out[i + ch] = v > 242 ? 255 : v < 0 ? 0 : v;
      }
    } else {
      let l = (0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2]) * k;
      if (filtro === 'cinza') { l = (l - 26) * (255 / 229); if (l > 240) l = 255; }
      else l = l >= 215 ? 255 : l <= 160 ? 0 : (l - 160) * (255 / 55);   // preto e branco com borda suave
      const v = l < 0 ? 0 : l > 255 ? 255 : l;
      out[i] = v; out[i + 1] = v; out[i + 2] = v;
    }
    out[i + 3] = 255;
  }
  return { w, h, d: out };
}

/** Acha a folha na foto sozinho: a maior área clara (papel) sobre um fundo mais escuro (mesa).
 *  Devolve os 4 cantos, ou null se não der para separar a folha do fundo (aí a pessoa ajusta). */
export function detectarCantos(src: Img): Cantos | null {
  const k = 256 / Math.max(src.w, src.h);
  const w = Math.max(8, Math.round(src.w * k)), h = Math.max(8, Math.round(src.h * k));
  const L = new Uint8Array(w * h);
  for (let y = 0; y < h; y++) for (let x = 0; x < w; x++) {
    const sx = Math.min(src.w - 1, Math.floor(x / k)), sy = Math.min(src.h - 1, Math.floor(y / k));
    const i = (sy * src.w + sx) * 4;
    L[y * w + x] = (0.299 * src.d[i] + 0.587 * src.d[i + 1] + 0.114 * src.d[i + 2]) | 0;
  }
  // limiar de Otsu (separa claro de escuro)
  const hist = new Array(256).fill(0); for (const v of L) hist[v]++;
  const total = w * h; let soma = 0; for (let i = 0; i < 256; i++) soma += i * hist[i];
  let sB = 0, wB = 0, melhor = 0, T = 128;
  for (let i = 0; i < 256; i++) {
    wB += hist[i]; if (!wB) continue; const wF = total - wB; if (!wF) break;
    sB += i * hist[i]; const mB = sB / wB, mF = (soma - sB) / wF;
    const v = wB * wF * (mB - mF) ** 2; if (v > melhor) { melhor = v; T = i; }
  }
  // maior mancha clara ligada
  const marca = new Int32Array(w * h).fill(-1);
  let maior = -1, tamMaior = 0;
  const fila = new Int32Array(w * h);
  for (let s = 0; s < w * h; s++) {
    if (L[s] <= T || marca[s] !== -1) continue;
    let ini = 0, fim = 0, n = 0; fila[fim++] = s; marca[s] = s;
    while (ini < fim) {
      const q = fila[ini++]; n++;
      const x = q % w, y = (q / w) | 0;
      if (x > 0 && L[q - 1] > T && marca[q - 1] === -1) { marca[q - 1] = s; fila[fim++] = q - 1; }
      if (x < w - 1 && L[q + 1] > T && marca[q + 1] === -1) { marca[q + 1] = s; fila[fim++] = q + 1; }
      if (y > 0 && L[q - w] > T && marca[q - w] === -1) { marca[q - w] = s; fila[fim++] = q - w; }
      if (y < h - 1 && L[q + w] > T && marca[q + w] === -1) { marca[q + w] = s; fila[fim++] = q + w; }
    }
    if (n > tamMaior) { tamMaior = n; maior = s; }
  }
  if (maior < 0 || tamMaior < total * 0.15 || tamMaior > total * 0.96) return null;
  // pontas: a soma e a diferença de x e y dão os 4 cantos de um quadrilátero
  let tl = { x: 0, y: 0, v: Infinity }, br = { x: 0, y: 0, v: -Infinity }, tr = { x: 0, y: 0, v: -Infinity }, bl = { x: 0, y: 0, v: Infinity };
  for (let q = 0; q < w * h; q++) {
    if (marca[q] !== maior) continue;
    const x = q % w, y = (q / w) | 0;
    if (x + y < tl.v) tl = { x, y, v: x + y }; if (x + y > br.v) br = { x, y, v: x + y };
    if (x - y > tr.v) tr = { x, y, v: x - y }; if (x - y < bl.v) bl = { x, y, v: x - y };
  }
  const P = [tl, tr, br, bl];
  // a área do quadrilátero tem de bater com a mancha (senão não é uma folha retangular)
  let area = 0; for (let i = 0; i < 4; i++) { const a = P[i], b = P[(i + 1) % 4]; area += a.x * b.y - b.x * a.y; }
  area = Math.abs(area) / 2;
  if (area < tamMaior * 0.85 || area > tamMaior * 1.25) return null;
  return P.map(p => ({ x: Math.min(1, Math.max(0, p.x / (w - 1))), y: Math.min(1, Math.max(0, p.y / (h - 1))) })) as Cantos;
}

/** Diminui (média dos pixels) para o lado maior caber em maxLado. */
export function diminuir(src: Img, maxLado: number): Img {
  const k = maxLado / Math.max(src.w, src.h);
  if (k >= 1) return src;
  const W = Math.max(1, Math.round(src.w * k)), H = Math.max(1, Math.round(src.h * k));
  const out = new Uint8Array(W * H * 4);
  const sx = src.w / W, sy = src.h / H;
  for (let y = 0; y < H; y++) {
    const y0 = Math.floor(y * sy), y1 = Math.max(y0 + 1, Math.floor((y + 1) * sy));
    for (let x = 0; x < W; x++) {
      const x0 = Math.floor(x * sx), x1 = Math.max(x0 + 1, Math.floor((x + 1) * sx));
      let r = 0, g = 0, b = 0, n = 0;
      for (let yy = y0; yy < y1; yy++) for (let xx = x0; xx < x1; xx++) { const i = (yy * src.w + xx) * 4; r += src.d[i]; g += src.d[i + 1]; b += src.d[i + 2]; n++; }
      const o = (y * W + x) * 4;
      out[o] = r / n; out[o + 1] = g / n; out[o + 2] = b / n; out[o + 3] = 255;
    }
  }
  return { w: W, h: H, d: out };
}

/** JPEG menor (para o PDF "arquivo menor" e para mandar à IA). */
export const jpegMenor = (bytes: Uint8Array, maxLado: number, qualidade: number) => codificar(diminuir(decodificar(bytes), maxLado), qualidade);
export const ehJpeg = (b: Uint8Array) => b[0] === 0xff && b[1] === 0xd8;
export const ehPng = (b: Uint8Array) => b[0] === 0x89 && b[1] === 0x50 && b[2] === 0x4e && b[3] === 0x47;

/** Tudo de uma vez: endireitar, girar e o filtro. */
export function tratar(src: Img, p: { cantos?: Cantos | null; giro?: number; filtro?: Filtro }, maxLado = 2000): Img {
  let img = p.cantos && !cantosInteiros(p.cantos) ? endireitar(src, p.cantos, maxLado) : src;
  img = girar(img, p.giro ?? 0);
  return filtrar(img, p.filtro ?? 'original');
}
